import { useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getGuideBoatsListing, getGuideCategoryToBoats } from "store/boats"
import { dataSource as equipmentTableDataSource } from "../equipmentTable"
import { DSIcon } from "ui/Icon/DSIcon"

/**
 * Custom hook for managing equipment data fetching and transformation
 * @returns {Object} Equipment data, loading state, and category information
 */
export const useEquipmentData = () => {
  const dispatch = useDispatch()
  const boatsListing = useSelector((state) => state.boats.items)
  const boatsLoading = useSelector((state) => state.boats.loading)
  const categoryListing = useSelector((state) => state.boats.category_to_boats.items)

  useEffect(() => {
    dispatch(getGuideBoatsListing())
    dispatch(getGuideCategoryToBoats())
  }, [dispatch])

  // Map category id -> name for quick lookups
  const categoryMap = useMemo(() => {
    return categoryListing.reduce((acc, cat) => {
      if (cat && (cat.id || cat.id === 0)) acc[String(cat.id)] = cat.name
      return acc
    }, {})
  }, [categoryListing])

  // Map API data to table format
  const equipmentData = useMemo(() => {
    if (boatsListing?.length) {
      return boatsListing.map((item) => {
        const rawCategory = item.category_id ?? item.category?.id ?? item.category
        const catIdStr = rawCategory == null ? null : String(rawCategory)
        const categoryName =
          (catIdStr && categoryMap[catIdStr]) || item.category_name || (typeof item.category === "string" ? item.category : "") || ""

        return {
          key: item.id,
          image: item.properties?.[0]?.preview_url ?? item.properties?.[0]?.url ?? <DSIcon name="boat" style={{ fontSize: 24 }} />,
          title: item.equipment_title || item.title || "",
          quantity: item.qantity || item.quantity || 0,
          asset_count: item.qantity || item.quantity || 0,
          category: categoryName,
          categoryId: catIdStr,
          // Both camelCase (for table) and snake_case (for card)
          assignedExperiences: item.assigned_experiences || 0,
          assigned_experiences: item.assigned_experiences || 0,
          upcomingBookings: item.upcoming_bookings || item.upcoming_booking || 0,
          upcoming_bookings: item.upcoming_bookings || item.upcoming_booking || 0,
          is_active: item.status === "active",
          status: item.status || ""
        }
      })
    }
    return equipmentTableDataSource
  }, [boatsListing, categoryMap])

  return {
    equipmentData,
    boatsLoading,
    categoryListing,
    categoryMap
  }
}
